import type { Sector } from '../../lib/assessment/types'

/**
 * Sector → topic routing for progressive disclosure.
 * Core topics are required; extended topics are optional but sector-relevant.
 */
export const SECTOR_CORE_TOPICS: Record<Sector, string[]> = {
  manufacturing: [
    'climate_ghg',
    'energy',
    'water',
    'waste',
    'pollution',
    'resource_use',
    'ohs',
    'labour',
    'procurement',
    'data_governance',
  ],
  construction: [
    'climate_ghg',
    'energy',
    'waste',
    'resource_use',
    'ohs',
    'heat_welfare',
    'labour',
    'procurement',
    'data_governance',
  ],
  real_estate: [
    'climate_ghg',
    'energy',
    'water',
    'refrigerants',
    'climate_adaptation',
    'customer_health_safety',
    'data_governance',
    'ethics',
  ],
  logistics: [
    'climate_ghg',
    'energy',
    'waste',
    'ohs',
    'heat_welfare',
    'labour',
    'data_governance',
  ],
  oil_gas_energy: [
    'climate_ghg',
    'energy',
    'water',
    'pollution',
    'biodiversity',
    'ohs',
    'climate_adaptation',
    'regulatory_management',
    'data_governance',
  ],
  hospitality: [
    'climate_ghg',
    'energy',
    'water',
    'waste',
    'refrigerants',
    'labour',
    'customer_health_safety',
    'procurement',
    'data_governance',
  ],
  retail: [
    'climate_ghg',
    'energy',
    'waste',
    'refrigerants',
    'procurement',
    'customer_health_safety',
    'data_privacy',
    'data_governance',
  ],
  healthcare: [
    'climate_ghg',
    'energy',
    'water',
    'waste',
    'refrigerants',
    'ohs',
    'customer_health_safety',
    'data_privacy',
    'data_governance',
  ],
  financial_services: [
    'climate_ghg',
    'energy',
    'ethics',
    'board_oversight',
    'risk_management',
    'data_governance',
    'cybersecurity',
    'transparency_claims',
  ],
  technology_services: [
    'climate_ghg',
    'energy',
    'data_privacy',
    'cybersecurity',
    'labour',
    'procurement',
    'data_governance',
    'ethics',
  ],
  agriculture: [
    'climate_ghg',
    'energy',
    'water',
    'waste',
    'biodiversity',
    'resource_use',
    'ohs',
    'heat_welfare',
    'data_governance',
  ],
  other: [
    'climate_ghg',
    'energy',
    'labour',
    'ethics',
    'data_governance',
    'risk_management',
  ],
}

export const SECTOR_EXTENDED_TOPICS: Record<Sector, string[]> = {
  manufacturing: [
    'refrigerants',
    'biodiversity',
    'climate_adaptation',
    'heat_welfare',
    'human_rights',
    'dei',
    'community',
    'board_oversight',
    'risk_management',
    'regulatory_management',
    'cybersecurity',
    'transparency_claims',
  ],
  construction: [
    'water',
    'pollution',
    'biodiversity',
    'climate_adaptation',
    'refrigerants',
    'human_rights',
    'community',
    'dei',
    'board_oversight',
    'risk_management',
    'regulatory_management',
    'transparency_claims',
  ],
  real_estate: [
    'waste',
    'pollution',
    'biodiversity',
    'resource_use',
    'labour',
    'dei',
    'community',
    'board_oversight',
    'risk_management',
    'cybersecurity',
    'procurement',
    'transparency_claims',
  ],
  logistics: [
    'water',
    'refrigerants',
    'pollution',
    'climate_adaptation',
    'human_rights',
    'dei',
    'procurement',
    'board_oversight',
    'risk_management',
    'cybersecurity',
    'transparency_claims',
  ],
  oil_gas_energy: [
    'waste',
    'refrigerants',
    'resource_use',
    'heat_welfare',
    'labour',
    'human_rights',
    'community',
    'ethics',
    'board_oversight',
    'cybersecurity',
    'procurement',
    'transparency_claims',
  ],
  hospitality: [
    'pollution',
    'climate_adaptation',
    'ohs',
    'heat_welfare',
    'human_rights',
    'dei',
    'community',
    'data_privacy',
    'board_oversight',
    'risk_management',
    'cybersecurity',
    'transparency_claims',
  ],
  retail: [
    'water',
    'pollution',
    'resource_use',
    'labour',
    'human_rights',
    'dei',
    'community',
    'board_oversight',
    'risk_management',
    'cybersecurity',
    'transparency_claims',
    'climate_adaptation',
  ],
  healthcare: [
    'pollution',
    'resource_use',
    'climate_adaptation',
    'labour',
    'human_rights',
    'dei',
    'community',
    'ethics',
    'board_oversight',
    'risk_management',
    'cybersecurity',
    'procurement',
    'transparency_claims',
  ],
  financial_services: [
    'water',
    'waste',
    'refrigerants',
    'climate_adaptation',
    'labour',
    'dei',
    'human_rights',
    'data_privacy',
    'procurement',
    'regulatory_management',
    'community',
    'customer_health_safety',
  ],
  technology_services: [
    'water',
    'waste',
    'refrigerants',
    'climate_adaptation',
    'ohs',
    'dei',
    'human_rights',
    'board_oversight',
    'risk_management',
    'regulatory_management',
    'transparency_claims',
    'customer_health_safety',
  ],
  agriculture: [
    'pollution',
    'refrigerants',
    'climate_adaptation',
    'labour',
    'human_rights',
    'community',
    'dei',
    'procurement',
    'board_oversight',
    'risk_management',
    'regulatory_management',
    'transparency_claims',
  ],
  other: [
    'water',
    'waste',
    'refrigerants',
    'ohs',
    'heat_welfare',
    'procurement',
    'data_privacy',
    'cybersecurity',
    'board_oversight',
    'climate_adaptation',
    'transparency_claims',
    'regulatory_management',
  ],
}

/** Sectors more likely to involve operational GHG “Source” characteristics under UAE climate framing. */
export const HIGHER_EMISSION_INTENSITY_SECTORS: Sector[] = [
  'manufacturing',
  'construction',
  'logistics',
  'oil_gas_energy',
  'hospitality',
  'real_estate',
  'agriculture',
  'healthcare',
]

export const ACTIVITY_EMISSION_SIGNALS = [
  'owns_fleet',
  'industrial_process',
  'owns_buildings',
  'uses_refrigerants',
  'generates_waste',
] as const
